"use client";

import React, { useEffect, useRef } from "react";
import { Button, CircleIconButton, Heading } from "@pamperedchef/design-system";
import { FocusScope } from "react-aria";

import * as styles from "./AddToCartSlider.module.scss";

export interface AddToCartSliderProps extends React.HTMLAttributes<HTMLDivElement> {
	isOpen: boolean;
	onClose: () => void;
	onViewCartAndCheckout: () => void;
	onContinueShopping: () => void;
	children?: React.ReactNode;
}

export function AddToCartSlider({
	isOpen,
	onClose,
	onViewCartAndCheckout,
	onContinueShopping,
	children,
	...props
}: AddToCartSliderProps): JSX.Element | null {
	const panelRef = useRef<HTMLDivElement | null>(null);

	// Click outside closes the slider
	useEffect(() => {
		if (!isOpen) return;

		function handleClick(e: MouseEvent) {
			if (
				panelRef.current &&
				!panelRef.current.contains(e.target as Node)
			) {
				onClose();
			}
		}

		document.addEventListener("mousedown", handleClick);
		return () => {
			document.removeEventListener("mousedown", handleClick);
		};
	}, [isOpen, onClose]);

	// Escape key closes the slider
	useEffect(() => {
		if (!isOpen) return;

		function onKeyDown(e: KeyboardEvent): void {
			if (e.key === "Escape") {
				onClose();
			}
		}

		window.addEventListener("keydown", onKeyDown);
		return () => {
			window.removeEventListener("keydown", onKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen) {
		return null;
	}

	return (
		<FocusScope contain restoreFocus autoFocus>
			<div className={styles.overlay} data-testid="atc-slider-overlay">
				<div
					{...props}
					ref={panelRef}
					role="dialog"
					aria-modal="true"
					aria-label="Item Added to Cart"
					className={styles.panel}
					data-testid="atc-slider-panel"
				>
					<div className={styles.header}>
						<CircleIconButton
							className={styles.closeButton}
							icon={{ name: "X" }}
							onClick={onClose}
							aria-label="Close"
						>
							Close
						</CircleIconButton>
						<Heading
							tag="h2"
							styledAs="h2"
							className={styles.title}
						>
							Item Added to Cart
						</Heading>
					</div>

					<div className={styles.content}>{children}</div>

					<div className={styles.footer}>
						<Button
							kind="primary"
							size="lg"
							onClick={onViewCartAndCheckout}
							className={styles.primaryButton}
						>
							View Cart & Checkout
						</Button>
						<Button
							kind="secondary"
							size="lg"
							onClick={onContinueShopping}
							className={styles.secondaryButton}
						>
							Continue Shopping
						</Button>
					</div>
				</div>
			</div>
		</FocusScope>
	);
}
